import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { State } from "../reducer";
import { LayoutAdminCafe, LayoutUserCafe } from "../layout/layout_cafe";
import { getCafeStatus } from "../api/cafe.api";
import { User } from "../interfaces/user";
import { canBypassClosure } from "../utils/access";
import { Channel, nextOpenAny, scheduleRows } from "../utils/schedule";
import { HalloweenArt } from "../theme/HalloweenArt";
import PancakeArena from "../components/PancakeArena";

const CHANNEL_META: Record<Channel, { emoji: string; label: string }> = {
  cafe:    { emoji: "☕", label: "Cafe" },
  bar:     { emoji: "🍹", label: "Bar" },
  dessert: { emoji: "🍰", label: "Dessert" },
};

const ClosedContent: React.FC = () => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const next = useMemo(() => nextOpenAny(now), [now]);

  // One row per channel × time-pattern, with days collapsed (e.g. "Mon–Thu").
  const allRows = useMemo(() => {
    const channels: Channel[] = ["cafe", "dessert", "bar"];
    return channels.flatMap((c) => scheduleRows(c).map((r) => ({ channel: c, ...r })));
  }, []);

  const nextLine = next
    ? (() => {
        const diffH = next.when.diff(now, "hours");
        const sameDay = moment(now).isSame(next.when, "day");
        const tomorrow = moment(now).add(1, "day").isSame(next.when, "day");
        if (sameDay) return `Opens today at ${next.when.format("HH:mm")}`;
        if (tomorrow) return `Opens tomorrow at ${next.when.format("HH:mm")}`;
        if (diffH < 7 * 24) return `Opens ${next.when.format("dddd [at] HH:mm")}`;
        return `Opens ${next.when.format("D MMM [at] HH:mm")}`;
      })()
    : "Hours coming soon";

  return (
    <div className="min-h-screen bg-white font-sans pb-[40px]">
      <div className="max-w-[760px] mx-auto">
      <header className="px-5 pt-[24px] pb-[16px]">
        <p className="eyebrow-v2 mb-2">We're closed</p>
        <h1 className="font-display font-black text-[28px] leading-[1.15] tracking-tightest text-ink-1000">
          The cafe is taking a break
        </h1>
        <p className="text-sm text-ink-500 mt-2">Come back during opening hours.</p>
      </header>

      {/* Hero card with next-open countdown */}
      <div className="px-5">
        <div className="rounded-3xl bg-hero text-hero-on p-6 relative overflow-hidden">
          <div className="absolute -right-4 -top-4 w-[140px] h-[140px] rounded-full" style={{ background: "var(--festive-1)", opacity: "var(--decor-opacity)" }} />
          <div className="relative">
            <h2 className="eyebrow-v2 text-hero-muted mb-2">Next opening</h2>
            <p className="font-display font-black text-[24px] leading-tight tracking-tight">
              {nextLine}
            </p>
            <p className="text-sm text-hero-muted mt-1.5">
              {next ? `${CHANNEL_META[next.window.channel].label} service` : "We'll be back soon"}
            </p>
          </div>
        </div>
      </div>

      <section className="px-5 pt-5" aria-labelledby="waiting-game-heading">
        <div className="card-v2 rounded-3xl p-4 sm:p-6 overflow-hidden">
          <div className="flex items-center gap-3 mb-3">
            <HalloweenArt variant="ghost-coffee" className="!w-12 !h-12" />
            <div>
              <h2 id="waiting-game-heading" className="font-display font-black text-xl text-ink-1000">เล่นระหว่างรอ ☕</h2>
              <p className="text-sm text-ink-500">ร้านกำลังพัก ลองซ้อนแพนเค้กให้สูงที่สุดระหว่างรอเปิดนะ</p>
            </div>
          </div>
          <PancakeArena />
        </div>
      </section>

      {/* Schedule */}
      <section className="px-5 pt-[24px]">
        <h2 className="eyebrow-v2 mb-2">Opening hours</h2>
        <div className="card-v2 divide-y divide-ink-100 overflow-hidden">
          {allRows.map((r, i) => (
            <div key={i} className="px-[16px] py-[14px] flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-xl flex-shrink-0">{CHANNEL_META[r.channel].emoji}</span>
                <div className="min-w-0">
                  <p className="font-display font-bold text-sm text-ink-1000">{CHANNEL_META[r.channel].label}</p>
                  <p className="text-xs text-ink-500">{r.days}</p>
                </div>
              </div>
              <span className="font-display font-bold text-sm text-ink-1000 tabular-nums">{r.time}</span>
            </div>
          ))}
          <div className="px-[16px] py-[14px] flex items-center gap-3">
            <span className="text-xl flex-shrink-0">😴</span>
            <div>
              <p className="font-display font-bold text-sm text-ink-1000">Sat &amp; Sun</p>
              <p className="text-xs text-ink-500">Closed all weekend</p>
            </div>
          </div>
        </div>
        <p className="text-[11px] text-ink-400 mt-3">All times shown in Bangkok local time (UTC+7).</p>
      </section>

      <footer className="text-[12px] gap-2 text-ink-400 flex pt-[40px] w-full justify-center">
        <p>Powered by</p>
        <a href="/team-credit" className="text-ink-700 font-semibold">DEK DUE CREW</a>
      </footer>
      </div>
    </div>
  );
};

const ClosePage: React.FC = () => {
  const user = useSelector((state: State) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const storeRedux = (u: User) => dispatch({ type: "LOGIN", payload: u });

  useEffect(() => {
    getCafeStatus().then((result) => {
      storeRedux({
        login: true,
        sub: "auth_google",
        id: user?.id,
        email: user?.email,
        name: user?.name,
        role: user?.role,
        isBarOpen: result?.isBarOpen,
        isCafeOpen: result?.isCafeOpen,
      });
      // Only `dev` bypasses the closed page now (admins follow user UX).
      if (canBypassClosure(user?.role)) {
        return navigate("/menuCafe-admin");
      }
      if (!result?.isCafeOpen && result?.isBarOpen) {
        return navigate("/order_bar");
      }
      // both open → redirect users to the right place
      if (result?.isCafeOpen) {
        return navigate(user?.role === "admin" ? "/menuCafe-admin" : "/menuCafe");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return user?.role === "admin" ? (
    <LayoutAdminCafe>
      <ClosedContent />
    </LayoutAdminCafe>
  ) : (
    <LayoutUserCafe>
      <ClosedContent />
    </LayoutUserCafe>
  );
};

export default ClosePage;
